import { FeatureDetectionRef } from "@/components/common/feature-detection-context";
import {
  isPanoObject,
  isPointCloudObject,
  UnknownObject,
} from "@/object-cache-type-guard";
import { selectActiveAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import type { GUID, TypedEvent } from "@faro-lotv/foundation";
import { ThreeEvent } from "@react-three/fiber";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type JSX,
} from "react";
import type { Vector3 } from "three";
import { AnnotationPicker } from "./annotation-picker/annotation-picker";
import { ManualBlurringTool } from "./manual-blurring-tool/manual-blurring-tool";
import { MultiPointMeasures } from "./multi-point-measures/multi-point-measures";
import { PointCloudAnalysisLabelTool } from "./point-cloud-analysis-label-tool";
import { PointCloudAnalysisTool } from "./point-cloud-analysis-tool";
import { ToolControlsRef } from "./tool-controls-interface";
import { SurfaceWandTool } from "./surface-wand/surface-wand-tool";

/** Parameters for onActiveToolChanged. */
export type ActiveToolAndPicking = {
  /** Active tool, or null when no active tool. */
  activeTool: ToolName | null;

  /** True if the active tool requires picking. */
  isPickingToolActive: boolean;
};

/** An object with callbacks to interact with the tools */
export type PickingToolsRef = {
  /** Forward model hovered event to the active tool */
  onModelHovered(
    ev: ThreeEvent<PointerEvent>,
    iElementId: string,
    features?: FeatureDetectionRef,
  ): void;

  /** Forward model click events to the active tool */
  onModelClicked(
    ev: ThreeEvent<MouseEvent>,
    iElementId: string,
    features?: FeatureDetectionRef,
  ): void;

  /** Forward model zoom events to the active tool */
  onModelZoomed(ev: ThreeEvent<MouseEvent>, iElementId: string): void;
};

export type PickingToolsProps = {
  /** The current active model */
  activeModels: UnknownObject[] | null;

  /**
   * Enable/Disable the measurement controls
   *
   * @default true
   */
  enableControls?: boolean;

  /**
   * Callback to signal that active tool has changed.
   *
   * @param activeToolAndPicking new active tool and picking
   */
  onActiveToolChanged?(activeToolAndPicking: ActiveToolAndPicking): void;

  /** Callback issued when the hovered point changes */
  currentPointChanged?(point: Vector3 | undefined): void;

  /** Optional signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens?: TypedEvent<GUID>;
};

/**
 * @returns True if the event was triggered on a valid element
 * @param activeModels The list of valid models
 * @param iElementId The id of the element from which the event was generated
 */
function isEventOnActiveModels(
  activeModels: UnknownObject[] | null,
  iElementId: string,
): boolean {
  return (
    !!activeModels && !!activeModels.find((e) => e.iElement.id === iElementId)
  );
}

export const PickingTools = forwardRef<PickingToolsRef, PickingToolsProps>(
  function PickingTools(
    {
      activeModels,
      enableControls,
      onActiveToolChanged,
      currentPointChanged,
      onValidClickInZoomLens,
    },
    ref,
  ): JSX.Element {
    // Forward events to the active tool
    const measurementToolRef = useRef<ToolControlsRef>(null);
    const annotationPickerRef = useRef<ToolControlsRef>(null);
    const pointCloudAnalysisToolRef = useRef<ToolControlsRef>(null);
    const surfaceWandRef = useRef<ToolControlsRef>(null);

    const toolRefMap = useMemo(
      () =>
        new Map([
          [ToolName.annotation, annotationPickerRef],
          [ToolName.measurement, measurementToolRef],
          [ToolName.analysis, pointCloudAnalysisToolRef],
          [ToolName.surfaceWand, surfaceWandRef],
          // Analysis label tool does not need `ref`, still need to keep in
          // the map to have disable exploration controls behaviour
          [ToolName.analysisLabel, null],
        ]),
      [],
    );

    // Notify if a tool is active to disable exploration controls click to focus
    const activeTool = useAppSelector(selectActiveTool);
    const isPickingToolActive =
      activeTool !== null && toolRefMap.has(activeTool);
    useEffect(() => {
      onActiveToolChanged?.({ activeTool, isPickingToolActive });
    }, [activeTool, isPickingToolActive, onActiveToolChanged]);

    useImperativeHandle(ref, () => ({
      onModelClicked(ev, iElementId, features) {
        if (ev.delta > 1 || !activeTool) return;
        if (!isEventOnActiveModels(activeModels, iElementId)) {
          return;
        }
        const toolRef = toolRefMap.get(activeTool);
        if (toolRef?.current?.pointClicked) {
          toolRef.current.pointClicked(ev, iElementId, features);
        }
      },
      onModelHovered(ev, iElementId, features) {
        if (!activeTool) return;
        const toolRef = toolRefMap.get(activeTool);
        if (!isEventOnActiveModels(activeModels, iElementId)) {
          return;
        }
        if (toolRef?.current?.pointHovered) {
          toolRef.current.pointHovered(ev, iElementId, features);
        }
      },
      onModelZoomed(ev, iElementId) {
        if (!activeTool) return;
        const toolRef = toolRefMap.get(activeTool);
        if (!isEventOnActiveModels(activeModels, iElementId)) {
          return;
        }
        if (toolRef?.current?.pointZoomed) {
          toolRef.current.pointZoomed(ev, iElementId);
        }
      },
    }));

    const activeModelsIds = useMemo(
      () => activeModels?.map((e) => e.iElement.id),
      [activeModels],
    );

    const activePointCloud = useMemo(
      () => activeModels?.find(isPointCloudObject),
      [activeModels],
    );

    const activePano = useMemo(
      () => activeModels?.find(isPanoObject),
      [activeModels],
    );

    const activeAnalysis = useAppSelector(selectActiveAnalysis);

    return (
      <>
        {activeModelsIds && (
          <MultiPointMeasures
            enableControls={enableControls}
            currentPointChanged={currentPointChanged}
            onValidClickInZoomLens={onValidClickInZoomLens}
            ref={measurementToolRef}
          />
        )}
        <AnnotationPicker
          isActive={activeTool === ToolName.annotation}
          activeModels={activeModels}
          currentPointChanged={currentPointChanged}
          onValidClickInZoomLens={onValidClickInZoomLens}
          ref={annotationPickerRef}
        />
        {activePano && (
          <ManualBlurringTool
            isActive={activeTool === ToolName.manualBlurring}
            activePano={activePano}
          />
        )}
        {activePointCloud && (
          <SurfaceWandTool
            activePointCloud={activePointCloud}
            ref={surfaceWandRef}
          />
        )}
        {activePointCloud && (
          <PointCloudAnalysisTool
            activePointCloud={activePointCloud}
            ref={pointCloudAnalysisToolRef}
          />
        )}
        {activePointCloud && activeAnalysis && (
          <PointCloudAnalysisLabelTool
            isActive={activeTool === ToolName.analysisLabel}
            activeAnalysis={activeAnalysis}
            activePointCloud={activePointCloud}
          />
        )}
      </>
    );
  },
);
